import axios, { AxiosError, AxiosHeaders } from 'axios';
import * as Sentry from '@sentry/react';
import { IThrownException } from '@acorn-portal/shared/src/services/interface/INetworkError';
import getAuthSession from '../utils/get-auth-session';
import { getUserManager } from '../utils/get-user-manager';

export const typeThrownException = <TThrown extends IThrownException>(
	toBeDetermined: unknown,
): toBeDetermined is TThrown => {
	const casted = toBeDetermined as TThrown;
	return (typeof casted.message === 'string' || typeof casted.detail === 'string') && typeof casted.status === 'number';
};

const API = axios.create();

enum Tenant {
	Acorn = 'Acorn',
	Carrot = 'Carrot',
	Motorcade = 'Motorcade',
	Flag = 'Flag',
	StreetCover = 'StreetCover',
	MyPolicy = 'MyPolicy',
}

const tenantMap: Record<string, Tenant> = {
	acorn: Tenant.Acorn,
	carrot: Tenant.Carrot,
	motorcade: Tenant.Motorcade,
	flag: Tenant.Flag,
	streetcover: Tenant.StreetCover,
	mypolicy: Tenant.MyPolicy,
};

const getTenantHeader = (): Tenant => {
	const app = import.meta.env.VITE_JOURNEY ?? '';

	const appNameLower = app.toLowerCase();

	const matchedTenant = Object.keys(tenantMap).find((key) => appNameLower.includes(key));

	if (!matchedTenant) {
		throw new TypeError(`No matching tenant found for app name: ${app}`);
	}

	return tenantMap[matchedTenant];
};

API.interceptors.request.use(
	(request) => {
		// Any status code that lie within the range of 2xx cause this function to trigger
		// Do something with response data
		if (!request.headers) {
			request.headers = {} as AxiosHeaders;
		}

		request.headers['X-Tenant'] = getTenantHeader();

		if (import.meta.env.VITE_USE_AUTH === 'true') {
			const user = getAuthSession();

			const token = user?.access_token;

			if (token) {
				request.headers.Authorization = `Bearer ${token}`;
			}
		}
		return request;
	},
	(error: AxiosError) => {
		// Any status codes that falls outside the range of 2xx cause this function to trigger
		// Do something with response error
		return Promise.reject(error);
	},
);

API.interceptors.response.use(
	(response) => response,
	async (error) => {
		let context: Record<string, unknown> = {};

		if (axios.isAxiosError(error)) {
			context = {
				message: error.message,
				name: error.name,
				stack: error.stack,
				url: error.config?.url,
				method: error.config?.method,
				headers: error.config?.headers,
				params: error.config?.params,
				data: error.config?.data,
			};

			if (error.response) {
				context = {
					...context,
					status: error.response.status,
					statusText: error.response.statusText,
					responseHeaders: error.response.headers,
					responseData: error.response.data,
				};
			}
		} else {
			context = {
				message: error.message ?? 'Unknown error',
				stack: error.stack,
			};
		}

		if (typeThrownException(error)) {
			context = {
				...context,
				status: error.status,
				detail: error.detail ?? error.message,
			};

			Sentry.captureMessage(`Thrown Exception: ${error.status}`, {
				level: 'warning',
				extra: context,
			});
		} else {
			Sentry.captureException(error, {
				extra: context,
			});
		}
		if (error.status === 401) {
			try {
				const userManager = getUserManager();
				const user = getAuthSession();
				await userManager.signoutRedirect({
					post_logout_redirect_uri: window.location.href,
					id_token_hint: user?.id_token,
				});
			} catch (signoutError) {
				Sentry.captureException(signoutError, {
					extra: {
						message: 'Error during signout redirect after 401',
						originalError: error,
					},
				});
			}
		}

		if (error.status === 403) {
			window.location.assign('/');
		}

		return Promise.reject(error);
	},
);
export default API;
